import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClientOptions } from '../../lib/refonteApi';

/** ⌘K / Ctrl+K — palette de recherche : pages + clients. La réponse au « où est X ? »
 *  (audit UX 09/10 : les actions existaient mais restaient introuvables). Sans dépendance. */
interface Cmd { label: string; hint?: string; to: string }

const CommandPalette: React.FC<{ pages: Cmd[] }> = ({ pages }) => {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const [clients, setClients] = useState<Cmd[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen(o => !o); setQ(''); setIdx(0); }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => { if (open) { inputRef.current?.focus(); if (!clients.length) getClientOptions().then(cs => setClients(cs.map((c: any) => ({ label: c.name, hint: 'client', to: `/app/clients/${c.id}` })))).catch(() => {}); } }, [open]); // eslint-disable-line

  const norm = (x: string) => x.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const results = useMemo(() => {
    const nq = norm(q.trim());
    const all = [...pages, ...clients];
    if (!nq) return pages.slice(0, 8);
    return all.filter(c => norm(c.label).includes(nq)).slice(0, 10);
  }, [q, pages, clients]);

  useEffect(() => { setIdx(0); }, [q]);
  if (!open) return null;

  const go = (c: Cmd) => { setOpen(false); nav(c.to); };

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-start justify-center pt-[12vh] p-4" onClick={() => setOpen(false)}>
      <div className="bg-panel border border-line rounded-card w-full max-w-lg shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <input
          ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(i + 1, results.length - 1)); }
            if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(i => Math.max(i - 1, 0)); }
            if (e.key === 'Enter' && results[idx]) go(results[idx]);
          }}
          placeholder="Chercher un client, une page…  (↑↓ puis Entrée)"
          className="w-full px-4 py-3 text-[14px] bg-transparent outline-none border-b border-line"
        />
        <div className="max-h-[320px] overflow-y-auto py-1">
          {results.length === 0 && <p className="px-4 py-3 text-[12.5px] text-faint">Aucun résultat.</p>}
          {results.map((c, i) => (
            <button key={c.to + c.label} onMouseEnter={() => setIdx(i)} onClick={() => go(c)}
              className={`w-full text-left px-4 py-2 text-[13px] flex items-center gap-2 ${i === idx ? 'bg-panel-2' : ''}`}>
              <span className="flex-1">{c.label}</span>
              {c.hint && <span className="font-mono text-[10px] text-faint">{c.hint}</span>}
            </button>
          ))}
        </div>
        <div className="px-4 py-2 border-t border-line font-mono text-[9.5px] text-faint">⌘K pour ouvrir · Échap pour fermer</div>
      </div>
    </div>
  );
};
export default CommandPalette;
